import { useCurrentUser } from '@/modules/authentication/hooks/useCurrentUser';
import { PresentationApprovalRequirement } from '@/types/schema';

import { useTenantDetailsContext } from '../TenantDetailsContext';

/**
 * Returns the review that the tenant requires for presentations: attestation
 * only, or attestation and approval. Returns null when the tenant requires
 * no review, the tenant configuration has not loaded, or the current user is
 * a collaborator. The review workflow does not apply to collaborators: they
 * see no review actions, no review status, and no DRAFT watermark. A user who
 * is both an employee and a collaborator
 * authenticates as an employee, so the workflow applies to them.
 */
export function usePresentationApprovalRequirement(): PresentationApprovalRequirement | null {
  const { applicationConfiguration } = useTenantDetailsContext();
  const requirement = applicationConfiguration?.requirePresentationApproval;
  const currentUser = useCurrentUser();
  const isCollaborator = !!currentUser?.collaborator && !currentUser.employee;

  if (
    isCollaborator ||
    !requirement ||
    requirement === PresentationApprovalRequirement.NotRequired
  ) {
    return null;
  }
  return requirement;
}

/**
 * usePresentationApprovalWorkflowEnabled is true when the tenant requires
 * attestation or approval for presentations and the user is not a collaborator.
 */
export function usePresentationApprovalWorkflowEnabled(): boolean {
  return usePresentationApprovalRequirement() !== null;
}
